/**
 * «Главное» вверху вкладки — выборка из готового отчёта: самая посещаемая
 * плохая страница, самый ошибающийся запрос. Оценки и вердикты пришли
 * с сайта, здесь только выбирается, о чём сказать первым.
 *
 * Факт — {tone: 'bad' | 'warn' | 'good' | 'neutral', text}.
 */
import {RUM_METRICS, METRIC_TEXT, formatMetric, formatMs, formatPercent, formatCount, formatChange} from "./rumFormat";

// Меньше этого — строка случайна, говорить о ней первой незачем.
const MIN_API_REQUESTS = 50
const MIN_SEGMENT_SHARE = 0.05

// Сайт не ставит оценку строке, пока по ней меньше 30 замеров (MIN_SAMPLES
// в clientFrontend/server/utils/rum/report.ts). «Нет оценки» — это не «хорошо».
const LOW_DATA_TEXT = 'Замеров пока мало: оценка появляется от 30 замеров на строку. До тех пор смотрите на цветные полоски под цифрами.'

/** Показатель, по которому строке хуже всего: больше всего посетителей в красной зоне. */
function worstMetric(metrics) {
    let worst = null
    RUM_METRICS.forEach(m => {
        const s = metrics?.[m]
        if (!s || !s.count) return
        if (!worst || s.poor > metrics[worst].poor) worst = m
    })
    return worst
}

const describeMetric = (metrics, m) => `${METRIC_TEXT[m].name.toLowerCase()} ${formatMetric(m, metrics[m].p75)}`

function pages(data) {
    const rows = [...(data.rows || [])].sort((a, b) => b.views - a.views)
    if (!rows.length) return []
    const facts = []

    const poor = rows.filter(r => r.verdict === 'poor')
    const ni = rows.filter(r => r.verdict === 'ni')
    const flagged = poor.length ? poor : ni
    if (flagged.length) {
        const top = flagged[0]
        const m = worstMetric(top.metrics)
        facts.push({
            tone: poor.length ? 'bad' : 'warn',
            text: `${poor.length ? 'Плохо' : 'Терпимо'} на страницах: ${flagged.length}. Самая посещаемая из них — «${top.label}»`
                + (m ? ` (${describeMetric(top.metrics, m)} у 75%)` : '') + '.',
        })
    } else if (rows.some(r => r.verdict === 'good')) {
        const unrated = rows.filter(r => !r.verdict).length
        facts.push({
            tone: 'good',
            text: unrated
                ? `Все страницы с оценкой в зелёной зоне. Ещё у ${unrated} мало замеров для оценки.`
                : 'Все страницы в зелёной зоне.',
        })
    } else {
        facts.push({tone: 'neutral', text: LOW_DATA_TEXT})
    }

    // Ухудшение среди десяти самых посещаемых: на редких страницах проценты скачут.
    let worse = null
    rows.slice(0, 10).forEach(row => {
        RUM_METRICS.forEach(m => {
            const c = row.changes?.[m]
            if (c && c.significant && c.direction === 'worse' && (!worse || c.rel > worse.change.rel)) {
                worse = {row, m, change: c}
            }
        })
    })
    if (worse) {
        facts.push({
            tone: 'warn',
            text: `Хуже, чем в прошлом периоде: «${worse.row.label}» — ${METRIC_TEXT[worse.m].name.toLowerCase()} ${formatChange(worse.change).text}.`,
        })
    }

    return facts
}

function audience(data) {
    const facts = []
    const devices = data.devices || []
    const mobile = devices.find(d => d.key === 'mobile')
    const desktop = devices.find(d => d.key === 'desktop')
    const a = mobile?.metrics?.LCP?.p75
    const b = desktop?.metrics?.LCP?.p75
    if (a && b && a / b >= 1.3) {
        facts.push({
            tone: 'warn',
            text: `На телефонах главное появляется в ${(a / b).toFixed(1)} раза дольше, чем на компьютерах: ${formatMetric('LCP', a)} против ${formatMetric('LCP', b)}.`,
        })
    }

    const poor = []
    const segments = data.segments || []
    segments.forEach(seg => {
        (seg.rows || []).forEach(row => {
            if (row.verdict === 'poor' && row.share >= MIN_SEGMENT_SHARE) poor.push({seg, row})
        })
    })
    poor.sort((x, y) => y.row.views - x.row.views)
    if (poor.length) {
        const {seg, row} = poor[0]
        facts.push({
            tone: 'bad',
            text: `Хуже всего группе «${row.label}» (${seg.name.toLowerCase()}): ${formatPercent(row.share)} просмотров, оценка «Плохо».`,
        })
    }

    if (!facts.length) {
        const rated = segments.some(seg => (seg.rows || []).some(row => row.verdict))
        if (rated) facts.push({tone: 'good', text: 'Заметных перекосов между группами посетителей нет.'})
        else if (devices.length || segments.length) facts.push({tone: 'neutral', text: LOW_DATA_TEXT})
    }
    return facts
}

const RELEASE_PHRASE = {
    better: 'быстрее предыдущего',
    worse: 'медленнее предыдущего',
    mixed: 'что-то стало быстрее, что-то медленнее',
    same: 'без заметных изменений',
}

function releases(data) {
    const latest = (data.rows || [])[0]
    if (!latest) return []
    const phrase = RELEASE_PHRASE[latest.verdict]
    if (!phrase) return [{tone: 'neutral', text: `Свежий релиз ${latest.label}: пока мало данных для сравнения.`}]

    const tone = {better: 'good', worse: 'bad', mixed: 'warn'}[latest.verdict] || 'neutral'
    const where = latest.routeChanges?.[0] ? ` Сильнее всего изменилось на «${latest.routeChanges[0].label}».` : ''

    return [{tone, text: `Свежий релиз ${latest.label}: ${phrase}.${where}`}]
}

function actions(data) {
    const rows = (data.rows || []).filter(r => r.enough)
    if (!(data.rows || []).length) return []
    if (!rows.length) return [{tone: 'neutral', text: 'Для сравнения быстрых и медленных просмотров пока мало данных — возьмите период подлиннее.'}]

    const top = [...rows].sort((a, b) => (b.missed || 0) - (a.missed || 0))[0]
    if (top.missed && top.lift > 0) {
        return [{
            tone: 'warn',
            text: `При быстрой загрузке «${top.label}» делают на ${Math.round(top.lift * 100)}% чаще. Из-за медленной упущено примерно ${formatCount(top.missed)}.`,
        }]
    }

    return [{tone: 'good', text: 'Скорость заметно не влияет на целевые действия.'}]
}

function product(data) {
    const marks = data.marks || []
    if (!marks.length) return []
    const bad = marks.filter(m => m.verdict === 'poor' || m.verdict === 'ni')
    if (!bad.length) return [{tone: 'good', text: 'Кнопки и переходы срабатывают быстро.'}]

    return bad.map(m => ({
        tone: m.verdict === 'poor' ? 'bad' : 'warn',
        text: `«${m.name}»: у 75% — ${formatMs(m.p75)}, быстро только у ${formatPercent(m.goodShare)}.`,
    }))
}

function api(data) {
    const rows = (data.rows || []).filter(r => r.count >= MIN_API_REQUESTS)
    if (!rows.length) return []
    const facts = []

    const failing = [...rows].sort((a, b) => b.errorRate - a.errorRate)[0]
    if (failing.errorRate >= 0.05) {
        facts.push({tone: 'bad', text: `Чаще всего ошибается ${failing.endpoint}: ${formatPercent(failing.errorRate)} запросов.`})
    }
    const slowest = [...rows].sort((a, b) => (b.p75 || 0) - (a.p75 || 0))[0]
    if (slowest.p75) {
        facts.push({
            tone: slowest.p75 >= 1000 ? 'warn' : 'neutral',
            text: `Дольше всего отвечает ${slowest.endpoint}: ${formatMs(slowest.p75)} у 75%.`,
        })
    }

    return facts
}

function scripts(data) {
    const rows = data.rows || []
    if (!rows.length) return []
    const own = rows.find(r => r.host === 'self')
    const external = rows.filter(r => r.host !== 'self').sort((a, b) => b.blockingPerView - a.blockingPerView)[0]
    if (!external || !external.blockingPerView) return [{tone: 'good', text: 'Внешние сервисы почти не занимают браузер.'}]

    const heavier = own && external.blockingPerView > own.blockingPerView
    return [{
        tone: heavier ? 'warn' : 'neutral',
        text: `Из внешних сервисов браузер сильнее всего занимает «${external.label}»: ${formatMs(external.blockingPerView)} на просмотр`
            + (own ? `, свой код — ${formatMs(own.blockingPerView)}.` : '.'),
    }]
}

function health(data) {
    const total = data.total
    if (!total || !total.views) return []
    const facts = []
    if (total.notMountedRate > 0) {
        facts.push({tone: 'bad', text: `У ${formatPercent(total.notMountedRate)} просмотров сайт так и не запустился.`})
    }
    if (total.chunkRate > 0) {
        facts.push({tone: 'warn', text: `У ${formatPercent(total.chunkRate)} просмотров не догрузился код сайта — обычно после деплоя.`})
    }
    facts.push({
        tone: total.errorRate >= 0.1 ? 'warn' : 'neutral',
        text: `Хотя бы одна ошибка в браузере — у ${formatPercent(total.errorRate)} просмотров.`,
    })

    return facts
}

const BUILDERS = {pages, audience, releases, actions, product, api, scripts, health}

export function factsFor(tab, data) {
    const build = BUILDERS[tab]
    if (!build || !data) return []
    try {
        return build(data)
    } catch (e) {
        // «Главное» — подсказка; если формат отчёта поменялся, таблица ниже всё равно покажется.
        return []
    }
}
